import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion, useReducedMotion } from "../../../lib/motion.js";
import { cn } from "../../../lib/utils.js";
import {
  templateDrawerEnterPose,
  templateDrawerGeometry,
  type TemplateDrawerFrame,
  type TemplateDrawerGeometry,
} from "../template-picker.js";

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function measureFrame(frame: TemplateDrawerFrame): TemplateDrawerGeometry {
  return templateDrawerGeometry({
    host: frame.host.getBoundingClientRect(),
    thread: frame.thread.getBoundingClientRect(),
    details: frame.details?.getBoundingClientRect() ?? null,
  });
}

/** Re-measures while open so the picker follows pane animations and resizes. */
function useFrameGeometry(
  frame: TemplateDrawerFrame | null,
  open: boolean,
): TemplateDrawerGeometry | null {
  const [geometry, setGeometry] = useState<TemplateDrawerGeometry | null>(null);

  useLayoutEffect(() => {
    if (!frame || !open) return;
    const update = () => setGeometry(measureFrame(frame));
    update();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
    observer?.observe(frame.host);
    observer?.observe(frame.thread);
    if (frame.details) observer?.observe(frame.details);
    window.addEventListener("resize", update);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [frame, open]);

  return frame && open ? geometry : null;
}

export function ConversationTemplateDrawer({
  open,
  titleId,
  descriptionId,
  closeDisabled,
  frame = null,
  onOpenChange,
  onCloseAutoFocus,
  children,
}: {
  open: boolean;
  titleId: string;
  descriptionId: string;
  closeDisabled: boolean;
  /** When set, the picker portals into `frame.host` and spans the thread (+ docked Details). */
  frame?: TemplateDrawerFrame | null;
  onOpenChange: (open: boolean) => void;
  onCloseAutoFocus?: (event: Event) => void;
  children: React.ReactNode;
}): React.JSX.Element {
  const reduceMotion = useReducedMotion();
  const panelRef = useRef<HTMLDivElement>(null);
  const hadOpenSessionRef = useRef(false);
  const [enterCommitted, setEnterCommitted] = useState(false);
  const geometry = useFrameGeometry(frame, open);
  const duration = reduceMotion ? 0 : 0.2;
  const enterPose = templateDrawerEnterPose({
    reduceMotion,
    enterCommitted,
  });

  if (!open && enterCommitted) {
    setEnterCommitted(false);
  }

  useEffect(() => {
    if (!open || reduceMotion) return;
    const frameId = requestAnimationFrame(() => {
      setEnterCommitted(true);
    });
    return () => cancelAnimationFrame(frameId);
  }, [open, reduceMotion]);

  const framed = frame != null;
  const visible = open && (!framed || geometry != null);

  useEffect(() => {
    if (!visible) return;
    hadOpenSessionRef.current = true;
    panelRef.current?.focus({ preventScroll: true });

    function handleKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape") {
        if (closeDisabled) return;
        event.preventDefault();
        event.stopPropagation();
        onOpenChange(false);
        return;
      }
      if (event.key !== "Tab" || !panelRef.current) return;
      const focusable = [...panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)];
      if (focusable.length === 0) {
        event.preventDefault();
        panelRef.current.focus({ preventScroll: true });
        return;
      }
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const active = document.activeElement;
      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus({ preventScroll: true });
        return;
      }
      if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    }

    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [visible, closeDisabled, onOpenChange]);

  const layer = (
    <AnimatePresence
      onExitComplete={() => {
        if (!hadOpenSessionRef.current) return;
        hadOpenSessionRef.current = false;
        onCloseAutoFocus?.(new Event("focus", { cancelable: true }));
      }}
    >
      {visible ? (
        <motion.button
          key="template-drawer-scrim"
          type="button"
          tabIndex={-1}
          aria-hidden="true"
          className={cn(
            "absolute z-10 cursor-default border-0 bg-[rgb(9_30_27/0.08)] p-0",
            framed ? "rounded-[14px]" : "inset-0",
          )}
          style={geometry?.scrim}
          initial={false}
          animate={{ opacity: enterPose === "resting" ? 1 : 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration }}
          disabled={closeDisabled}
          onClick={() => {
            if (!closeDisabled) onOpenChange(false);
          }}
        />
      ) : null}
      {visible ? (
        <motion.div
          key="template-drawer-panel"
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={descriptionId}
          tabIndex={-1}
          className={cn(
            "absolute z-20 flex flex-col overflow-hidden rounded-2xl border border-inbox-line bg-surface-elevated text-foreground shadow-[0_24px_60px_-18px_rgb(6_37_29/0.32),0_2px_8px_rgb(6_37_29/0.06)] outline-none",
            !framed && "inset-x-2.5 bottom-2.5 h-[min(35rem,calc(100%-4.5rem))] min-h-[17.5rem]",
          )}
          style={geometry?.panel}
          initial={false}
          animate={
            enterPose === "resting"
              ? { opacity: 1, y: 0, scale: 1 }
              : { opacity: 0, y: 10, scale: 0.99 }
          }
          exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 10, scale: 0.99 }}
          transition={{ duration, ease: [0.22, 1, 0.36, 1] }}
        >
          {children}
        </motion.div>
      ) : null}
    </AnimatePresence>
  );

  return frame ? createPortal(layer, frame.host) : layer;
}
